// tslint:disable: max-line-length
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { DocumentDatabaseService } from 'fitforce-document-sync';
import { DatabaseModel, DatabaseQuery, DatabaseRecord, DatabaseRecordIdentifier, DatabaseType, OfflineService as OfflineFirstOfflineService } from 'fitforce-offline-first';
import { filter, isNil } from 'lodash';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserDatabaseService } from './user-database.service';
// tslint:enable: max-line-length

export enum OfflineReason {
  ONLINE = 'Currently online.',
  FORCED = 'User manually set to offline mode.',
  BAD_NETWORK = 'Bad network connectivity to Fitforce server.',
  NO_NETWORK = 'The device is unable to access the Internet.',
}

@Injectable({
  providedIn: 'root',
})
export class OfflineService extends OfflineFirstOfflineService {
  constructor(
    private userDatabase: UserDatabaseService,
    private documentDatabase: DocumentDatabaseService,
    private http: HttpClient,
  ) {
    super();

    this.userDatabase.connect();
    this.documentDatabase.connect();
  }

  public getRecord<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseRecordIdentifier | DatabaseQuery): Observable<T> {
    return from(this.database(model).connect().then(() => {
      return this.database(model).get<T>(model, where);
    }));
  }

  public getAll<T extends DatabaseRecord>(model: DatabaseModel): Observable<T[]> {
    return from(
      this.database(model).connect().then(() => {
        return this.database(model).getAll<T>(model);
      })
    );
  }

  public clear(model: DatabaseModel): Observable<void> {
    return from(
      this.database(model).connect().then(() => {
        return this.database(model).clear(model);
      })
    );
  }

  public query<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseQuery): Observable<T[]> {
    return from(this.database(model).query(model, where) as Promise<T[]>);
  }

  public queryUndeleted<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseQuery): Observable<T[]> {
    return from(this.database(model).query(model, where) as Promise<T[]>).pipe(map((records: T[]) => {
      return filter(records, (record: T) => {
        return isNil(record.deletedAt);
      });
    }));
  }

  public save(model: DatabaseModel, records: DatabaseRecord | DatabaseRecord[]): Observable<void> {
    return from(this.database(model).connect().then(() => {
      return this.database(model).insert(model, records, true);
    }));
  }

  public delete(model: DatabaseModel, record: DatabaseRecord): Observable<void> {
    return from(this.database(model).connect().then(() => {
      return this.database(model).delete(model, record);
    }));
  }

  public deleteByKey(model: DatabaseModel, key: string): Observable<void> {
    return from(this.database(model).connect().then(() => {
      return this.database(model).deleteByKey(model.objectStoreName, key);
    }));
  }

  public count(model: DatabaseModel): Observable<number> {
    return from(this.database(model).connect().then(() => {
      return this.database(model).count(model.objectStoreName);
    }));
  }

  public clearUserDatabase(): Observable<void> {
    return from(
      this.userDatabase.connect().then(() => {
        return this.userDatabase.clearDatabase();
      })
    );
  }

  private database(model: DatabaseModel) {
    if (model.databaseType === DatabaseType.User) {
      return this.userDatabase;
    } else if (model.databaseType === DatabaseType.Document) {
      return this.documentDatabase;
    } else {
      throw new Error(`Model ${model.recordName} has no database mapping`);
    }
  }

  /**
   * Pings the server only if the app is online
   */
  tryPingServer() {
    if (this.isOnline() && this.isNavigatorOnline()) {
      this.pingServer();
    }
  }

  /**
   * MID-1762 - Ping the server to check if the browser is *really* online.
   * If the API endpoint is unreachable, force.
   * If the ping is greater than X, force offline.
   */
  pingServer() {
    const timeStart: number = performance.now();
    this.http.get(environment.applySubstitutions(environment.api_url + 'api/'), { responseType: 'text' }).subscribe(res => {
        const timeEnd: number = performance.now();
        const ping: number = timeEnd - timeStart;
        console.log('Ping to server:', ping);

        // if the ping was slow, assume poor connection and mark as offline due to bad network
        if (ping > 5000) {
          this.forceOffline(true, OfflineReason.BAD_NETWORK);
        }
      }, err => {
        this.forceOffline(true, OfflineReason.NO_NETWORK);
      });
  }
}
